import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useBusinessContext } from '../../../context/BusinessContext';
import { useAlert } from '../../../context/alert/AlertContext';
import { useValueVisibility } from '../../../context/ValueVisibilityContext';
import LoadingComponent from '../../commons/LoadingComponent';
import ConfirmModal from '../../commons/confirm-modal/ConfirmModal';
import { TeamMember } from '../../../model/teammember/TeamMember';
import { Service } from '../../../model/service/Service';
import { getTeamMembersByBusinessId } from '../../../service/teammember/team-member-service';
import { getServicesByBusinessId } from '../../../service/service/service-service';
import { getCommissionDetails, markCommissionPaid } from '../../../service/payment/transaction-service';
import { CommissionRecord } from '../../../model/teammember/Commission';
import { currencyCode } from '../../../util/money';
import SalariesTab from './SalariesTab';
import '../payments/PaymentsComponent.css';
import './PayrollComponent.css';
import './SalariesTab.css';

// --- Date presets (same set/behaviour as the Payments screen) ---------------
type DatePreset =
    | 'TODAY' | 'YESTERDAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'LAST_MONTH'
    | 'THIS_QUARTER' | 'LAST_QUARTER' | 'THIS_YEAR' | 'LAST_YEAR' | 'CUSTOM';

const DATE_PRESET_KEYS: DatePreset[] = [
    'TODAY', 'YESTERDAY', 'THIS_WEEK', 'THIS_MONTH', 'LAST_MONTH',
    'THIS_QUARTER', 'LAST_QUARTER', 'THIS_YEAR', 'LAST_YEAR', 'CUSTOM',
];

const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const presetRange = (p: DatePreset): { start: string; end: string } => {
    const t = new Date();
    const y = t.getFullYear();
    const m = t.getMonth();
    const mondayOffset = (t.getDay() + 6) % 7;
    switch (p) {
        case 'TODAY': return { start: ymd(t), end: ymd(t) };
        case 'YESTERDAY': { const d = new Date(t); d.setDate(t.getDate() - 1); return { start: ymd(d), end: ymd(d) }; }
        case 'THIS_WEEK': {
            const s = new Date(t); s.setDate(t.getDate() - mondayOffset);
            const e = new Date(s); e.setDate(s.getDate() + 6);
            return { start: ymd(s), end: ymd(e) };
        }
        case 'THIS_MONTH': return { start: ymd(new Date(y, m, 1)), end: ymd(new Date(y, m + 1, 0)) };
        case 'LAST_MONTH': return { start: ymd(new Date(y, m - 1, 1)), end: ymd(new Date(y, m, 0)) };
        case 'THIS_QUARTER': { const q = Math.floor(m / 3) * 3; return { start: ymd(new Date(y, q, 1)), end: ymd(new Date(y, q + 3, 0)) }; }
        case 'LAST_QUARTER': {
            const q = Math.floor(m / 3) * 3 - 3; const qy = q < 0 ? y - 1 : y; const rs = ((q % 12) + 12) % 12;
            return { start: ymd(new Date(qy, rs, 1)), end: ymd(new Date(qy, rs + 3, 0)) };
        }
        case 'THIS_YEAR': return { start: ymd(new Date(y, 0, 1)), end: ymd(new Date(y, 11, 31)) };
        case 'LAST_YEAR': return { start: ymd(new Date(y - 1, 0, 1)), end: ymd(new Date(y - 1, 11, 31)) };
        default: return { start: '', end: '' };
    }
};

const fmtMoneyRaw = (n: number, currency = 'USD'): string => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n); }
    catch { return `${currency} ${n.toFixed(2)}`; }
};

type StatusFilter = 'ALL' | 'PENDING' | 'PAID';
type PayrollTab = 'commissions' | 'salaries';

/**
 * Payroll — Comissões + Salários tabs.
 * Gated by PAYROLL permission (menu + backend).
 */
const PayrollComponent: React.FC = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<PayrollTab>('commissions');
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const { hidden, toggle: toggleVisibility } = useValueVisibility();

    const businessId = selectedBusiness?.id ? String(selectedBusiness.id) : '';
    const businessCurrency = currencyCode(selectedBusiness?.currency);

    const fmtMoney = (n: number, currency = businessCurrency): string =>
        hidden ? 'R$ ••••' : fmtMoneyRaw(n, currency);

    const initial = useMemo(() => presetRange('THIS_MONTH'), []);
    const [datePreset, setDatePreset] = useState<DatePreset>('THIS_MONTH');
    const [startDate, setStartDate] = useState<string>(initial.start);
    const [endDate, setEndDate] = useState<string>(initial.end);
    const [tmFilter, setTmFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

    const [entries, setEntries] = useState<CommissionRecord[]>([]);
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [services, setServices] = useState<Service[]>([]);
    const [loading, setLoading] = useState(true);

    const [payTarget, setPayTarget] = useState<CommissionRecord | null>(null);
    const [payDate, setPayDate] = useState<string>(ymd(new Date()));
    const [payBusy, setPayBusy] = useState(false);

    // --- Per-row action busy (for Desfazer) ---
    const [actionBusy, setActionBusy] = useState<string | null>(null);

    // --- Mark-all commissions paid ---
    const [markAllCommOpen, setMarkAllCommOpen] = useState(false);
    const [markAllCommBusy, setMarkAllCommBusy] = useState(false);

    const reload = useCallback(async () => {
        if (!businessId) return;
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const [data, tms, svc] = await Promise.all([
                getCommissionDetails(businessId, startDate || null, endDate || null, tmFilter || null, token),
                getTeamMembersByBusinessId(businessId, token).catch(() => [] as TeamMember[]),
                Number(businessId)
                    ? getServicesByBusinessId(Number(businessId), token).catch(() => [] as Service[])
                    : Promise.resolve([] as Service[]),
            ]);
            setEntries(data);
            setMembers(tms);
            setServices(svc);
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.commissions.alerts.failLoad'));
        } finally {
            setLoading(false);
        }
    }, [businessId, startDate, endDate, tmFilter, getAccessTokenSilently, setAlert]);

    useEffect(() => { reload(); }, [reload]);

    const memberName = (id?: string | null) => {
        const tm = members.find((m) => String(m.id) === String(id));
        return tm ? `${tm.firstName ?? ''} ${tm.lastName ?? ''}`.trim() || '—' : (id ? `#${id}` : '—');
    };
    const serviceName = (id?: string | null) =>
        services.find((s) => String(s.id) === String(id))?.name ?? (id ? '—' : '—');

    // Status filter applied client-side over the period+member result set.
    const visible = entries.filter((c) =>
        statusFilter === 'ALL' ? true : statusFilter === 'PAID' ? !!c.paidAt : !c.paidAt
    );
    const total = visible.reduce((s, c) => s + (c.amount ?? 0), 0);
    const paid = visible.reduce((s, c) => s + (c.paidAt ? (c.amount ?? 0) : 0), 0);
    const pending = total - paid;

    const confirmMarkPaid = async () => {
        if (!payTarget?.id) return;
        try {
            setPayBusy(true);
            const token = await getAccessTokenSilently();
            await markCommissionPaid(String(payTarget.id), payDate, token);
            setAlert('success', t('payroll.commissions.alerts.markedPaid'));
            setPayTarget(null);
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.commissions.alerts.failMarkPaid'));
        } finally {
            setPayBusy(false);
        }
    };

    const handleUnmarkCommission = async (c: CommissionRecord) => {
        if (!c.id) return;
        try {
            setActionBusy(String(c.id));
            const token = await getAccessTokenSilently();
            await markCommissionPaid(String(c.id), null, token);
            setAlert('success', t('payroll.commissions.alerts.unmarked'));
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.commissions.alerts.failUnmark'));
        } finally {
            setActionBusy(null);
        }
    };

    const pendingCommissions = visible.filter((c) => !c.paidAt);

    const confirmMarkAllCommPaid = async () => {
        if (pendingCommissions.length === 0) return;
        try {
            setMarkAllCommBusy(true);
            const token = await getAccessTokenSilently();
            const today = ymd(new Date());
            await Promise.all(
                pendingCommissions.map((c) => markCommissionPaid(String(c.id), today, token))
            );
            setAlert('success', t('payroll.commissions.alerts.allMarked', { count: pendingCommissions.length }));
            setMarkAllCommOpen(false);
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.commissions.alerts.failMarkPaid'));
        } finally {
            setMarkAllCommBusy(false);
        }
    };

    return (
        <div className="payments-page" data-cy="payroll-page">

            {/* ===== Tab strip + eye toggle ===== */}
            <div className="gs-tab-strip" style={{ display: 'flex', alignItems: 'center' }}>
                {([
                    { key: 'commissions', label: t('payroll.tabs.commissions') },
                    { key: 'salaries',    label: t('payroll.tabs.salaries') },
                ] as { key: PayrollTab; label: string }[]).map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`gs-tab ${activeTab === tab.key ? 'gs-tab--active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                        data-cy={`payroll-tab-${tab.key}`}
                    >
                        {tab.label}
                    </button>
                ))}
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    style={{ marginLeft: 'auto' }}
                    onClick={toggleVisibility}
                    title={hidden ? t('payroll.showValues') : t('payroll.hideValues')}
                    data-cy="payroll-eye-toggle"
                >
                    <FontAwesomeIcon icon={hidden ? faEyeSlash : faEye} />
                </button>
            </div>

            {/* ===== Tab: Comissões ===== */}
            {activeTab === 'commissions' && (
                <>
                    {/* ---------- Filters + summary (one card) ---------- */}
                    <div className="list-card payroll-controls">
                        <div className="payments-toolbar">
                            <div className="payments-toolbar__filters" style={{ flex: 1 }}>
                                <label>
                                    {t('payroll.commissions.filters.period')}
                                    <select
                                        value={datePreset}
                                        data-cy="payroll-filter-period"
                                        onChange={(e) => {
                                            const next = e.target.value as DatePreset;
                                            setDatePreset(next);
                                            if (next !== 'CUSTOM') {
                                                const r = presetRange(next);
                                                setStartDate(r.start);
                                                setEndDate(r.end);
                                            }
                                        }}
                                    >
                                        {DATE_PRESET_KEYS.map((k) => (
                                            <option key={k} value={k}>{t('payments.presets.' + k)}</option>
                                        ))}
                                    </select>
                                </label>
                                {datePreset === 'CUSTOM' && (
                                    <>
                                        <label>{t('payroll.commissions.filters.from')}<input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
                                        <label>{t('payroll.commissions.filters.to')}<input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></label>
                                    </>
                                )}
                                <label>
                                    {t('payroll.commissions.filters.teamMember')}
                                    <select value={tmFilter} onChange={(e) => setTmFilter(e.target.value)}>
                                        <option value="">{t('payroll.commissions.filters.allMembers')}</option>
                                        {members.map((m) => (
                                            <option key={m.id} value={String(m.id)}>
                                                {`${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || `#${m.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    {t('payroll.commissions.filters.status')}
                                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)} data-cy="payroll-filter-status">
                                        <option value="ALL">{t('payroll.commissions.filters.all')}</option>
                                        <option value="PENDING">{t('payroll.commissions.filters.pending')}</option>
                                        <option value="PAID">{t('payroll.commissions.filters.paid')}</option>
                                    </select>
                                </label>
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
                                disabled={pendingCommissions.length === 0 || loading}
                                title={pendingCommissions.length === 0 ? t('payroll.commissions.markAllPaidNoneTitle') : t('payroll.commissions.markAllPaidTitle', { count: pendingCommissions.length })}
                                onClick={() => setMarkAllCommOpen(true)}
                                data-cy="payroll-mark-all-paid-btn"
                            >
                                {t('payroll.commissions.markAllPaid')}
                            </button>
                        </div>

                        {/* Summary of the filtered set — inline strip under the filters. */}
                        <div className="payroll-summary">
                            <div className="payroll-summary__item">
                                <span className="payroll-summary__label">{t('payroll.commissions.summary.total')}</span>
                                <span className="payroll-summary__value">{fmtMoney(total, businessCurrency)}</span>
                            </div>
                            <div className="payroll-summary__item">
                                <span className="payroll-summary__label">{t('payroll.commissions.summary.paid')}</span>
                                <span className="payroll-summary__value">{fmtMoney(paid, businessCurrency)}</span>
                            </div>
                            <div className="payroll-summary__item">
                                <span className="payroll-summary__label">{t('payroll.commissions.summary.pending')}</span>
                                <span className="payroll-summary__value payroll-summary__value--neg">{fmtMoney(pending, businessCurrency)}</span>
                            </div>
                            <div className="payroll-summary__item">
                                <span className="payroll-summary__label">{t('payroll.commissions.summary.entries')}</span>
                                <span className="payroll-summary__value">{visible.length}</span>
                            </div>
                        </div>
                    </div>

                    {/* ---------- Detail table (one row per commission) ---------- */}
                    <div className="list-card payments-table-card">
                        <div className="payments-table-wrap">
                            <table className="payments-table">
                                <thead>
                                    <tr>
                                        <th>{t('payroll.commissions.table.when')}</th>
                                        <th>{t('payroll.commissions.table.teamMember')}</th>
                                        <th>{t('payroll.commissions.table.appointment')}</th>
                                        <th>{t('payroll.commissions.table.service')}</th>
                                        <th className="payments-table__num">{t('payroll.commissions.table.base')}</th>
                                        <th className="payments-table__num">{t('payroll.commissions.table.rate')}</th>
                                        <th className="payments-table__num">{t('payroll.commissions.table.commission')}</th>
                                        <th>{t('payroll.commissions.table.status')}</th>
                                        <th>{t('payroll.commissions.table.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr><td colSpan={9}><LoadingComponent /></td></tr>
                                    )}
                                    {!loading && visible.length === 0 && (
                                        <tr><td colSpan={9} className="empty-hint">{t('payroll.commissions.empty')}</td></tr>
                                    )}
                                    {!loading && visible.map((c) => {
                                        const cur = c.currency || businessCurrency;
                                        const when = c.createdOn ? new Date(c.createdOn) : null;
                                        const paidWhen = c.paidAt ? new Date(c.paidAt) : null;
                                        return (
                                            <tr key={String(c.id)} data-cy={`payroll-commission-row-${c.id}`}>
                                                <td>{when ? when.toLocaleDateString() : '—'}</td>
                                                <td>{memberName(c.teamMemberId)}</td>
                                                <td>{c.appointmentId ? `#${c.appointmentId}` : '—'}</td>
                                                <td>{serviceName(c.serviceId)}</td>
                                                <td className="payments-table__num">{fmtMoney(c.baseAmount ?? 0, cur)}</td>
                                                <td className="payments-table__num">{(c.rate ?? 0)}%</td>
                                                <td className="payments-table__num">{fmtMoney(c.amount ?? 0, cur)}</td>
                                                <td>
                                                    {paidWhen ? (
                                                        <span className="salaries-status salaries-status--paid">
                                                            {t('payroll.commissions.status.paid', { date: paidWhen.toLocaleDateString() })}
                                                        </span>
                                                    ) : (
                                                        <span className="salaries-status salaries-status--pending">
                                                            {t('payroll.commissions.status.pending')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {paidWhen ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-link text-secondary p-0"
                                                            disabled={actionBusy === String(c.id)}
                                                            onClick={() => handleUnmarkCommission(c)}
                                                            data-cy={`payroll-unmark-commission-${c.id}`}
                                                        >
                                                            {actionBusy === String(c.id) ? '…' : t('payroll.commissions.actions.undo')}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-link p-0"
                                                            onClick={() => { setPayDate(ymd(new Date())); setPayTarget(c); }}
                                                            data-cy={`payroll-mark-paid-commission-${c.id}`}
                                                        >
                                                            {t('payroll.commissions.actions.markPaid')}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <ConfirmModal
                        open={!!payTarget}
                        title={t('payroll.commissions.confirmMarkPaid.title')}
                        confirmLabel={t('payroll.commissions.confirmMarkPaid.confirm')}
                        busy={payBusy}
                        onConfirm={confirmMarkPaid}
                        onCancel={() => { if (!payBusy) setPayTarget(null); }}
                    >
                        {payTarget && (
                            <div>
                                <p style={{ marginBottom: '0.75rem' }}>
                                    {t('payroll.commissions.confirmMarkPaid.body', {
                                        name: memberName(payTarget.teamMemberId),
                                        appt: payTarget.appointmentId ? ` (agendamento #${payTarget.appointmentId})` : '',
                                        amount: fmtMoney(payTarget.amount ?? 0, payTarget.currency || businessCurrency),
                                    })}
                                </p>
                                <label className="form-label">{t('payroll.commissions.confirmMarkPaid.dateLabel')}</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    style={{ maxWidth: 200 }}
                                    value={payDate}
                                    onChange={(e) => setPayDate(e.target.value)}
                                />
                            </div>
                        )}
                    </ConfirmModal>

                    <ConfirmModal
                        open={markAllCommOpen}
                        title={t('payroll.commissions.confirmMarkAll.title')}
                        confirmLabel={t('common.confirm')}
                        cancelLabel={t('common.cancel')}
                        busy={markAllCommBusy}
                        onConfirm={confirmMarkAllCommPaid}
                        onCancel={() => { if (!markAllCommBusy) setMarkAllCommOpen(false); }}
                        message={
                            <>
                                {t('payroll.commissions.confirmMarkAll.message', { count: pendingCommissions.length })}
                            </>
                        }
                    />
                </>
            )}

            {/* ===== Tab: Salários ===== */}
            {activeTab === 'salaries' && <SalariesTab />}
        </div>
    );
};

export default PayrollComponent;
